import type {
  breakdownProcess,
  childPartV2Param,
  partProcess,
  partOtherProcess,
} from "@/api/breakdownService";
import { parseDecimal } from "@/utils/inputParse";
import { useMemo } from "react";
import Decimal from "decimal.js";

export const useCalculateTotalMaterialCost = (
  childPart: childPartV2Param,
  materialCostInput: string,
  isAdminPct: boolean,
  isProfitPct: boolean,
) => {
  const materialCost = useMemo(() => {
    const cost = childPart.material_price || 0;
    const consumption = childPart.consumption || 0;
    const materialCost = new Decimal(cost).times(consumption).toNumber();
    if (childPart.is_purchase_part) {
      return parseDecimal(materialCostInput, 2);
    }
    return materialCost;
  }, [
    childPart.material_price,
    childPart.consumption,
    childPart.is_purchase_part,
    materialCostInput,
  ]);

  const returnScrapPrice = useMemo(() => {
    const scrapPrice = childPart.scrap_price || 0;
    const consumption = childPart.consumption || 0;
    const partWeight = childPart.part_weight || 0;
    const returnScrapPrice = new Decimal(consumption)
      .minus(partWeight)
      .times(scrapPrice)
      .toNumber();
    return returnScrapPrice;
  }, [childPart.scrap_price, childPart.consumption, childPart.part_weight]);

  const totalSecondTierProcessCost = useMemo(() => {
    const total =
      childPart?.child_part_material_second_tier_processes_v2?.reduce(
        (sum, p) => sum.plus(p.cost || 0),
        new Decimal(0),
      ) ?? new Decimal(0);
    return total.toNumber();
  }, [childPart.child_part_material_second_tier_processes_v2]);

  const totalOtherProcessCost = useMemo(() => {
    const total =
      childPart?.child_part_material_other_processes_v2?.reduce(
        (sum, p) => sum.plus(p.cost || 0),
        new Decimal(0),
      ) ?? new Decimal(0);
    return total.toNumber();
  }, [childPart.child_part_material_other_processes_v2]);

  const otherPercentCost = useMemo(() => {
    const otherPercentCost = new Decimal(materialCost || 0)
      .times(childPart?.percent || 0)
      .div(100)
      .toNumber();
    return otherPercentCost;
  }, [materialCost, childPart?.percent]);

  const totalMaterialCost = useMemo(() => {
    const qty = childPart.qty || 0;
    if (childPart.is_purchase_part) {
      return new Decimal(materialCost).times(qty).toNumber();
    }
    const sum = new Decimal(parseDecimal(materialCost, 2))
      .minus(parseDecimal(returnScrapPrice, 2))
      .plus(parseDecimal(totalSecondTierProcessCost, 2))
      .plus(parseDecimal(totalOtherProcessCost, 2))
      .plus(parseDecimal(otherPercentCost, 2));
    const adminCost = isAdminPct
      ? sum.times(childPart.admin || 0).div(100)
      : new Decimal(childPart.admin || 0);
    const profitCost = isProfitPct
      ? sum.times(childPart.profit || 0).div(100)
      : new Decimal(childPart.profit || 0);
    return sum.plus(adminCost).plus(profitCost).times(qty).toNumber();
  }, [
    childPart.admin,
    childPart.profit,
    childPart.qty,
    childPart.is_purchase_part,
    materialCost,
    returnScrapPrice,
    totalSecondTierProcessCost,
    totalOtherProcessCost,
    otherPercentCost,
    isAdminPct,
    isProfitPct,
  ]);

  return {
    materialCost,
    returnScrapPrice,
    totalSecondTierProcessCost,
    totalOtherProcessCost,
    otherPercentCost,
    totalMaterialCost,
  };
};

export const useCalculateCPProcessCost = (
  childPart: childPartV2Param,
  childPartProcess: Array<breakdownProcess>,
) => {
  const cpProcessCost = useMemo(() => {
    return childPartProcess.map((process) => {
      const processDetail = childPart?.child_part_processes_v2?.find(
        (p) => p.part_process_v2_id === process.id,
      );
      const qty1 = processDetail?.qty_1 || 0;
      const costPerQty1 = processDetail?.cost_per_qty_1 || 0;
      const qty2 = processDetail?.qty_2 || 0;
      const costPerQty2 = processDetail?.cost_per_qty_2 || 0;
      const processCost = new Decimal(qty1)
        .times(costPerQty1)
        .plus(new Decimal(qty2).times(costPerQty2))
        .toNumber();
      return { id: process.id, cost: processCost };
    });
  }, [childPart?.child_part_processes_v2, childPartProcess]);

  const SumCPProcessCost = useMemo(() => {
    const totalCost = cpProcessCost
      .reduce((sum, p) => sum.plus(p.cost), new Decimal(0))
      .toNumber();
    return totalCost;
  }, [cpProcessCost]);

  const totalCPProcessCost = useMemo(() => {
    const totalCost = new Decimal(parseDecimal(SumCPProcessCost, 2))
      .times(childPart.qty || 0)
      .toNumber();
    return totalCost;
  }, [SumCPProcessCost, childPart.qty]);

  return { cpProcessCost, SumCPProcessCost, totalCPProcessCost };
};

export const useCalculatePartProcess = (
  partProcess: partProcess,
  isAdminPct: boolean,
  isProfitPct: boolean,
) => {
  const totalOEMPartProcess: Array<{ id: number; total_price: number }> =
    useMemo(() => {
      return (
        partProcess.oem_part_process?.map((process) => {
          const total_price = new Decimal(process.price_per_unit || 0)
            .times(process.unit || 0)
            .toNumber();
          return { id: process.id, total_price };
        }) || []
      );
    }, [partProcess.oem_part_process]);

  const totalSparePartProcess: Array<{ id: number; total_price: number }> =
    useMemo(() => {
      return (
        partProcess.spare_part_process?.map((process) => {
          const total_price = new Decimal(process.price_per_unit || 0)
            .times(process.unit || 0)
            .toNumber();
          return { id: process.id, total_price };
        }) || []
      );
    }, [partProcess.spare_part_process]);

  const totalOEMPartProcessCost = useMemo(() => {
    const totalOEMCost = totalOEMPartProcess.reduce(
      (sum, p) => sum.plus(p.total_price),
      new Decimal(0),
    );
    const adminCost = isAdminPct
      ? totalOEMCost.times(partProcess.admin_oem_part_process || 0).div(100)
      : new Decimal(partProcess.admin_oem_part_process || 0);
    const profitCost = isProfitPct
      ? totalOEMCost.times(partProcess.profit_oem_part_process || 0).div(100)
      : new Decimal(partProcess.profit_oem_part_process || 0);
    const totalCost = totalOEMCost.plus(adminCost).plus(profitCost);
    return parseDecimal(totalCost.toNumber(), 2);
  }, [
    totalOEMPartProcess,
    isAdminPct,
    partProcess.admin_oem_part_process,
    partProcess.profit_oem_part_process,
    isProfitPct,
  ]);

  const totalSparePartProcessCost = useMemo(() => {
    const totalSpareCost = totalSparePartProcess.reduce(
      (sum, p) => sum.plus(p.total_price),
      new Decimal(0),
    );
    const adminCost = isAdminPct
      ? totalSpareCost
          .times(partProcess.admin_spare_part_process || 0)
          .div(100)
      : new Decimal(partProcess.admin_spare_part_process || 0);
    const profitCost = isProfitPct
      ? totalSpareCost
          .times(partProcess.profit_spare_part_process || 0)
          .div(100)
      : new Decimal(partProcess.profit_spare_part_process || 0);
    const totalCost = totalSpareCost.plus(adminCost).plus(profitCost);
    return parseDecimal(totalCost.toNumber(), 2);
  }, [
    totalSparePartProcess,
    isAdminPct,
    partProcess.admin_spare_part_process,
    partProcess.profit_spare_part_process,
    isProfitPct,
  ]);

  return {
    totalOEMPartProcess,
    totalSparePartProcess,
    totalOEMPartProcessCost,
    totalSparePartProcessCost,
  };
};

export const useCalculatePartOtherProcess = (
  partOtherProcess: partOtherProcess,
) => {
  const totalOEMPartOtherProcessCost = useMemo(() => {
    const totalOEMCost =
      partOtherProcess.oem_other_process?.reduce(
        (sum, p) => sum.plus(p.price || 0),
        new Decimal(0),
      ) ?? new Decimal(0);
    return parseDecimal(totalOEMCost.toNumber(), 2);
  }, [partOtherProcess.oem_other_process]);

  const totalSparePartOtherProcessCost = useMemo(() => {
    const totalSpareCost =
      partOtherProcess.spare_other_process?.reduce(
        (sum, p) => sum.plus(p.price || 0),
        new Decimal(0),
      ) ?? new Decimal(0);
    return parseDecimal(totalSpareCost.toNumber(), 2);
  }, [partOtherProcess.spare_other_process]);

  return { totalOEMPartOtherProcessCost, totalSparePartOtherProcessCost };
};
